import { createContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { Box, Tab, Tabs, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { useEditConfigMenu } from '@components/ConfigEditMenu/hooks'
import { CopyDialog } from '@components/CopyDialog'
import { PageTitle } from '@components/PageTitle'
import { TabPanel } from '@components/Tabs'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, FLEXIBLE, VISIBLE_HIDDEN } from '@constants'

import { AllForms } from './components/AllForms'
import { FormGroupList } from './components/FormGroupList'
import { useConfigForms } from './hooks'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const ConfigForms = () => {
  const { t } = useTranslation()

  const configMenu = useEditConfigMenu(ENTITY_CONFIG_TYPE.FORMS)

  const {
    configMenuOption,
    handleSelectConfigMenuOption,
    setIsOpenScriptDialog,
    isExportAllSelected,
    handleSelectExportAll,
  } = configMenu

  const { state, data, handlers } = useConfigForms()

  const { currentSort, allForms, currentTab, copyFormData, showCopyDialog, isLoadingCopyForm } =
    state

  const { isFetchingForms, objects, isAllForms } = data

  const {
    handleDeleteForm,
    handleDeleteFormByCode,
    handleCopyForm,
    handleEditForm,
    handleSetObjectCode,
    handleSetSearchPattern,
    handleSetSorting,
    handleRefreshFormTablesData,
    handleTabChange,
    handleSetDirty,
    handleSetAllForms,
    handleCloseDialog,
    handleShowDialog,
  } = handlers

  const contextValue = {
    formTables: allForms,
    formsIsLoading: isFetchingForms,
    currentSort,
    objects,
    onDeleteForm: handleDeleteForm,
    onDeleteFormByCode: handleDeleteFormByCode,
    onCopyForm: handleCopyForm,
    onEditForm: handleEditForm,
    onSetObjectCode: handleSetObjectCode,
    onSetSearchPattern: handleSetSearchPattern,
    onSetSorting: handleSetSorting,
    onShowDialog: handleShowDialog,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.FORMS}>
        {showCopyDialog && copyFormData && (
          <CopyDialog
            copyData={copyFormData}
            dialogTitle={t('copying.form.dialogTitle')}
            isShow={showCopyDialog}
            loading={isLoadingCopyForm}
            models={allForms.map(form => form.code)}
            titleEntityType='form'
            onClose={handleCloseDialog}
            onCopy={handleCopyForm}
          />
        )}
        <Outlet
          context={{
            setFormTables: handleSetAllForms,
            refreshFormTablesData: handleRefreshFormTablesData,
          }}
        />
        <Box
          sx={{
            ...FLEXIBLE,
            ...(!isAllForms && VISIBLE_HIDDEN),
          }}
        >
          <PageTitle
            end={
              <>
                {currentTab !== 1 && (
                  <Box sx={{ py: 0.5 }}>
                    <EditConfigMenu />
                  </Box>
                )}
              </>
            }
            start={
              <>
                <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
                  {t('pageTitle.models')}
                </Typography>
                <Typography display={'inline'} variant={'subtitle2'}>
                  {t('pageTitle.modelsControl')}
                </Typography>
              </>
            }
          />
          <PageContentLayout key={currentTab}>
            <ExportAllCheckbox />
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label={t('configViews.all')} />
                <Tab label={t('configViews.groups')} />
              </Tabs>
            </Box>
            <TabPanel
              isAlwaysRendered
              contentSx={FLEXIBLE}
              hidden={false}
              sx={{
                ...FLEXIBLE,
                ...(currentTab !== 0 && VISIBLE_HIDDEN),
              }} // Пришлось кастомизировать стили, т.к display none немного ломает отображение DataGrid
              index={0}
              value={currentTab}
            >
              <AllForms />
            </TabPanel>
            <TabPanel
              hidden={false}
              index={1}
              sx={{ ...(currentTab !== 1 && VISIBLE_HIDDEN) }}
              value={currentTab}
            >
              <FormGroupList onSetDirty={handleSetDirty} />
            </TabPanel>
          </PageContentLayout>
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
